import { isNil } from "lodash";

import {
  EQUIPMENT_VALUE_TO_LABEL_MAP,
  SHIPMENT_SERVICE_VALUE_TO_LABEL_MAP,
  SHIPMENT_TYPE_VALUE_TO_LABEL_MAP,
} from "constants/index";
import { toFullReadableAddress } from "utils";
import { formatPrice, formatQuotingUnitOfMeasure } from "utils/quotes";

import type { CustomerListItem } from "components/SupplierNetwork/types";
import type {
  CreateShipmentFormState,
  DirectBookingAssignmentFormState,
} from "types/shipment";
// Shipment Type Labels

export interface FormattedShipmentSection {
  title: string;
  items: {
    label: string;
    value: string;
  }[];
  infoText?: string;
  warningText?: string;
}

function formatEquipmentTypes(equipmentTypes: string[]): string {
  if (equipmentTypes.length === 0) return "Not specified";
  const equipmentTypesLabel = equipmentTypes.map(
    (equipmentType) => EQUIPMENT_VALUE_TO_LABEL_MAP[equipmentType],
  );

  return equipmentTypesLabel.join(", ");
}

function formatServiceTypes(serviceType: string): string {
  const serviceTypesLabel = SHIPMENT_SERVICE_VALUE_TO_LABEL_MAP[serviceType];
  return serviceTypesLabel;
}

function formatShipmentType(shipmentType: string): string {
  return SHIPMENT_TYPE_VALUE_TO_LABEL_MAP[shipmentType] || shipmentType;
}

function formatCarrierPartners(
  emails: string[],
  followingCarriers: FollowingCarrier[],
): string {
  if (emails.length === 0) return "No partners selected";

  const formattedPartners = emails.map((email) => {
    // Try to find matching carrier in network
    const matchingCarrier = followingCarriers.find(
      (carrier) => carrier.user.email === email,
    );

    if (matchingCarrier?.company?.name) {
      return `${matchingCarrier.user.first_name} ${matchingCarrier.user.last_name[0]} (${matchingCarrier.company.name})`;
    }

    return email; // Final fallback
  });

  return formattedPartners.join(", ");
}

function formatWeight(weightLb: number | null): string {
  if (weightLb === null) {
    return "Not specified";
  }
  return `${weightLb.toLocaleString()} lbs`;
}

function formatRoutePoints(routePoints: EditableRoutePointState[]): string[] {
  return routePoints.map((point, index) => {
    let stopType = `Stop ${point.point_number}`;

    // First point is always Origin, last is always Destination
    if (index === 0) {
      stopType = "Origin";
    } else if (index === routePoints.length - 1) {
      stopType = "Destination";
    } else {
      stopType = `Stop ${index}`;
    }

    const location = point.point_location;
    const fullAddress = toFullReadableAddress(location);
    if (point.schedule_type === "ASAP") {
      return `${stopType}: ${fullAddress} → Flexible`;
    }

    const date = point.start_time
      ? new Date(`${point.start_time}T00:00:00`).toLocaleDateString()
      : "";
    return `${stopType}: ${fullAddress} → ${date}`;
  });
}

function formatNotificationType(type: ShipmentNotificationType): string {
  switch (type) {
    case "ALL_CARRIER_NETWORK":
      return "All carriers in your network and carriers with matched lanes";
    case "SELECTED_NETWORK_CARRIERS":
      return "Only Selected Partners and carriers with matched lanes";
    case "PRIVATE_SHIPMENT_ALL_NETWORK":
      return "All carriers in your network";
    case "PRIVATE_SHIPMENT":
      return "Only selected Partners and Groups within your network";
    default:
      return type;
  }
}

function formatShipmentVisibility(type: ShipmentNotificationType): string {
  switch (type) {
    case "ALL_CARRIER_NETWORK":
      return "Public";
    case "SELECTED_NETWORK_CARRIERS":
      return "Public";
    case "PRIVATE_SHIPMENT_ALL_NETWORK":
      return "Private";
    case "PRIVATE_SHIPMENT":
      return "Private";

    default:
      return type;
  }
}

const DIRECT_BOOKING_LABEL = "Direct Booking";

// The booked rate with its unit, and for a project how many loads it covers.
function formatDirectBookingRate(
  formState: CreateShipmentFormState,
  assignment: DirectBookingAssignmentFormState,
): string {
  const rate = formatPrice(
    assignment.price,
    assignment.currency,
    formState.quotingUnitOfMeasure,
  );
  if (formState.shipmentType !== "MULTI_SHIPMENT_PROJECT") {
    return rate;
  }
  const loads = formState.availableLoads === 1 ? "load" : "loads";
  return `${rate}, each load, ${formState.availableLoads ?? "-"} ${loads}`;
}

function formatDirectBookingAssignee(
  assignment: DirectBookingAssignmentFormState,
  followingCarriers: FollowingCarrier[],
): string {
  const name =
    assignment.carrierDisplayName ??
    formatCarrierPartners([assignment.carrierEmail], followingCarriers);
  return name === assignment.carrierEmail
    ? assignment.carrierEmail
    : `${name} (${assignment.carrierEmail})`;
}

function formatEmailNotificationType(
  type: ShipperEmailNotificationType,
): string {
  switch (type) {
    case "ALL":
      return "Enabled ";
    case "MUTE":
      return "Muted";
    default:
      return type;
  }
}

// Helper function for customer display name formatting with optional company
function formatCustomerDisplayName(
  customer: CustomerListItem,
  includeCompany = false,
): string {
  const name =
    `${customer.first_name ?? ""} ${customer.last_name ?? ""}`.trim() ||
    customer.email;

  if (includeCompany && customer.company_name) {
    return `${name} (${customer.company_name})`;
  }

  return name;
}

function formatSnapshotDisplayName(snapshot: TemplateCustomerSnapshot): string {
  const name =
    `${snapshot.first_name ?? ""} ${snapshot.last_name ?? ""}`.trim() ||
    snapshot.email;
  return snapshot.company_name ? `${name} (${snapshot.company_name})` : name;
}

function formatCustomerInfo(
  formState: CreateShipmentFormState,
  supplierCustomers: CustomerListItem[] = [],
): {
  recipientNames: string[];
  removedExternalCustomerNames: string[];
  disabledInternalCustomerNames: string[];
} {
  const totalCustomers =
    formState.customerRoleIds.length +
    formState.externalCustomerIds.length +
    formState.customerInviteEmails.length;

  if (totalCustomers === 0) {
    return {
      recipientNames: [],
      removedExternalCustomerNames: [],
      disabledInternalCustomerNames: [],
    };
  }

  // Create lookup maps for O(1) customer access instead of O(n) find operations
  const customersByRoleId = new Map<number, CustomerListItem>();
  const customersById = new Map<number, CustomerListItem>();

  supplierCustomers.forEach((customer) => {
    if (customer.type === "INTERNAL" && customer.role_id != null) {
      customersByRoleId.set(customer.role_id, customer);
    } else if (customer.type === "EXTERNAL" && customer.id != null) {
      customersById.set(customer.id, customer);
    }
  });

  // A template's own denormalized identity, used only as a display fallback
  // when an id no longer resolves against the live network above (see #2836).
  const snapshotsByRoleId = new Map<number, TemplateCustomerSnapshot>();
  const snapshotsByExternalId = new Map<number, TemplateCustomerSnapshot>();
  formState.customerSnapshots.forEach((snapshot) => {
    if (snapshot.type === "internal_customer") {
      snapshotsByRoleId.set(snapshot.role_id, snapshot);
    } else {
      snapshotsByExternalId.set(snapshot.id, snapshot);
    }
  });

  const recipientNames: string[] = [];
  const removedExternalCustomerNames: string[] = [];
  const disabledInternalCustomerNames: string[] = [];

  // Internal customers are still notified even if their partnership was
  // removed, since the underlying account isn't deleted -- just labeled so
  // the shipper knows why it's not in their live customer list. A customer
  // whose account is disabled is a separate case: still fully partnered, but
  // won't actually be notified, so it's called out as a warning below too.
  formState.customerRoleIds.forEach((roleId) => {
    const customer = customersByRoleId.get(roleId);
    if (customer) {
      const displayName = formatCustomerDisplayName(customer, true);
      if (customer.is_disabled) {
        recipientNames.push(
          `${displayName} (account disabled, won't be notified)`,
        );
        disabledInternalCustomerNames.push(displayName);
      } else {
        recipientNames.push(displayName);
      }
      return;
    }
    const snapshot = snapshotsByRoleId.get(roleId);
    if (snapshot) {
      recipientNames.push(
        `${formatSnapshotDisplayName(snapshot)} (removed from network)`,
      );
    }
  });

  // External customers are hard-deleted on removal, so one missing from the
  // live list will NOT be notified -- called out as a warning below.
  formState.externalCustomerIds.forEach((id) => {
    const customer = customersById.get(id);
    if (customer) {
      recipientNames.push(formatCustomerDisplayName(customer, true));
      return;
    }
    const snapshot = snapshotsByExternalId.get(id);
    const displayName = snapshot
      ? formatSnapshotDisplayName(snapshot)
      : "A removed customer";
    recipientNames.push(`${displayName} (won't be notified)`);
    removedExternalCustomerNames.push(displayName);
  });

  // Add email invites (no company information available)
  recipientNames.push(...formState.customerInviteEmails);

  return {
    recipientNames,
    removedExternalCustomerNames,
    disabledInternalCustomerNames,
  };
}

export function formatShipmentData(
  formState: CreateShipmentFormState,
  followingCarriers: FollowingCarrier[] = [],
  supplierCustomers: CustomerListItem[] = [],
): FormattedShipmentSection[] {
  const sections: FormattedShipmentSection[] = [];

  const assignment = formState.directBookingAssignment;

  // Posting Type Section (at the top)
  const visibilityItems = [
    {
      label: "Posting type",
      value:
        assignment === null
          ? formatShipmentVisibility(formState.notificationSettingsType)
          : DIRECT_BOOKING_LABEL,
    },
  ];

  visibilityItems.push({
    label: "Who will be notified",
    value:
      assignment === null
        ? formatNotificationType(formState.notificationSettingsType)
        : "Only the carrier you booked",
  });

  sections.push({
    title: "Posting Type",
    items: visibilityItems,
  });

  // Basic Information Section
  sections.push({
    title: "Basic Information",
    items: [
      {
        label: "Shipment Type",
        value: formatShipmentType(formState.shipmentType),
      },
      {
        label: "Pricing Mode",
        value: formatQuotingUnitOfMeasure(formState.quotingUnitOfMeasure),
      },
      {
        label: "Equipment Types",
        value: formatEquipmentTypes(formState.equipmentTypes),
      },
      { label: "Service Type", value: formatServiceTypes(formState.service) },
      { label: "Weight", value: formatWeight(formState.weightLb) },
      {
        label: "Commodity",
        value:
          formState.commodity || formState.commodityFreetext || "Not specified",
      },
    ],
  });

  // Route Information Section
  const routeStrings = formatRoutePoints(formState.routePoints);
  sections.push({
    title: "Route Information",
    items: routeStrings.map((route) => ({
      label: route.split(":")[0], // Extract the stop type (Origin, Stop 1, Destination)
      value: route.split(":").slice(1).join(":").trim(), // Extract the address and timing
    })),
  });

  // Pricing & Advanced Options Section
  const advancedItems: { label: string; value: string }[] = [];

  if (formState.isBookNow && formState.bookNowPrice) {
    advancedItems.push({
      label: "Book Now Price",
      value: formatPrice(
        formState.bookNowPrice,
        formState.bookNowCurrency || "",
        formState.quotingUnitOfMeasure,
      ),
    });
  }

  if (formState.customName) {
    advancedItems.push({
      label: "Custom Name",
      value: formState.customName,
    });
  }

  if (formState.specialRequirements) {
    advancedItems.push({
      label: "Special Requirements",
      value: formState.specialRequirements,
    });
  }

  if (formState.additionalDetails) {
    advancedItems.push({
      label: "Dimensions & Notes",
      value: formState.additionalDetails,
    });
  }

  if (advancedItems.length > 0) {
    sections.push({
      title: "Additional Details",
      items: advancedItems,
    });
  }

  // Project Information Section (for multi-shipment projects)
  if (formState.shipmentType === "MULTI_SHIPMENT_PROJECT") {
    sections.push({
      title: "Project Information",
      items: [
        {
          label: "Available Loads",
          value: isNil(formState.availableLoads)
            ? "Not specified"
            : formState.availableLoads.toString(),
        },
        { label: "Type", value: "Multi-Shipment Project" },
      ],
    });
  }

  // Carrier Partner Access Section (only for private shipments)
  const isPrivateShipment =
    formState.notificationSettingsType === "PRIVATE_SHIPMENT" ||
    formState.notificationSettingsType === "PRIVATE_SHIPMENT_ALL_NETWORK";

  // Confirmed partners and invited emails are listed together: the shipper
  // picked them the same way and is sending the shipment to all of them.
  const carrierPartnerEmails = [
    ...(formState.notificationSetingsEmails ?? []),
    ...(formState.networkInviteEmails ?? []),
  ];

  if (assignment !== null) {
    sections.push({
      title: DIRECT_BOOKING_LABEL,
      items: [
        {
          label: "Carrier",
          value: formatDirectBookingAssignee(assignment, followingCarriers),
        },
        {
          label: "Rate",
          value: formatDirectBookingRate(formState, assignment),
        },
      ],
    });
  } else if (isPrivateShipment && carrierPartnerEmails.length > 0) {
    sections.push({
      title: "Carrier Partner Access",
      items: [
        {
          label: "Authorized Partners",
          value: formatCarrierPartners(carrierPartnerEmails, followingCarriers),
        },
      ],
    });
  }

  // Notification Settings Section
  sections.push({
    title: "Notification Settings",
    items: [
      {
        label: "Email Notifications",
        value: formatEmailNotificationType(formState.emailNotificationType),
      },
    ],
  });

  // Customer Information Section
  const totalCustomers =
    formState.customerRoleIds.length +
    formState.externalCustomerIds.length +
    formState.customerInviteEmails.length;

  if (totalCustomers > 0) {
    const {
      recipientNames,
      removedExternalCustomerNames,
      disabledInternalCustomerNames,
    } = formatCustomerInfo(formState, supplierCustomers);
    const customerItems: { label: string; value: string }[] = [];

    customerItems.push({
      label: "Recipients",
      value: recipientNames.join(", "),
    });

    const warningSentences: string[] = [];
    if (removedExternalCustomerNames.length > 0) {
      warningSentences.push(
        `${removedExternalCustomerNames.join(", ")} ${
          removedExternalCustomerNames.length === 1 ? "was" : "were"
        } removed from your network and will NOT be notified for this shipment.`,
      );
    }
    if (disabledInternalCustomerNames.length > 0) {
      warningSentences.push(
        `${disabledInternalCustomerNames.join(", ")} ${
          disabledInternalCustomerNames.length === 1 ? "has" : "have"
        } a disabled account and will NOT be notified for this shipment.`,
      );
    }
    if (warningSentences.length > 0) {
      warningSentences.push(
        'Remove them from "Notify Customer(s)" if that wasn\'t intended.',
      );
    }

    sections.push({
      title: "Customer Information",
      items: customerItems,
      infoText:
        "Selected customers will receive automated tracking updates for this shipment.",
      warningText:
        warningSentences.length > 0 ? warningSentences.join(" ") : undefined,
    });
  }

  // Team Management & Access Section
  const teamItems: { label: string; value: string }[] = [];

  teamItems.push({
    label: "Team Members with Access",
    value:
      formState.shareeRoleIds.length > 0
        ? `You and ${formState.shareeRoleIds.length} other team member(s)`
        : "No team members added",
  });

  sections.push({
    title: "Team Management & Access",
    items: teamItems,
    infoText:
      "Branch and Company administrators also have permission to manage all shipments.",
  });

  // Documents & Attachments Section (always show)
  const docItems: { label: string; value: string }[] = [];
  let docInfoText: string | undefined;

  if (formState.files && formState.files.length > 0) {
    formState.files.forEach((file) => {
      docItems.push({
        label: file.name,
        value: `${(file.size / 1024).toFixed(1)} KB`,
      });
    });
    docInfoText = "Files will be visible to all carriers";
  } else {
    docItems.push({
      label: "Attachments",
      value: "No documents attached",
    });
  }

  sections.push({
    title: "Documents & Attachments",
    items: docItems,
    infoText: docInfoText,
  });

  return sections;
}
